import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation } from "wouter";
import AccountActions from "@/components/AccountActions";
import BrandLockup from "@/components/BrandLockup";
import { useAuth } from "@/contexts/AuthContext";
import { normalizeBookCode, rememberClaimOfferSkipped } from "@/lib/bookCode";
import { friendlyErrorMessage } from "@/lib/friendlyError";
import { normalizeReturnPath } from "@/lib/authRedirect";
import { supabase } from "@/lib/supabase";

const bookCodesTable = (client: NonNullable<typeof supabase>) =>
  (client as unknown as { from: (table: "book_codes") => any }).from("book_codes");
const claimClient = (client: NonNullable<typeof supabase>) =>
  client as unknown as { rpc: (name: "claim_book_code", args: { p_code: string }) => Promise<{ data: unknown; error: { message: string } | null }> };

type ClaimState = "checking" | "ready" | "claimed" | "error";

function messageFrom(error: unknown): string {
  return friendlyErrorMessage(error, "book-code");
}

export default function Claim() {
  const { session, isLoading, isConfigured } = useAuth();
  const [, navigate] = useLocation();
  const returnPath = normalizeReturnPath(new URLSearchParams(window.location.search).get("next"));
  const [claimState, setClaimState] = useState<ClaimState>("checking");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wasClaimed, setWasClaimed] = useState(false);
  const [checkKey, setCheckKey] = useState(0);

  useEffect(() => {
    if (isLoading || !session || !supabase || !isConfigured) return;
    let cancelled = false;
    setClaimState("checking");
    setError(null);
    const userId = session.user.id;

    async function checkExistingClaim() {
      try {
        const { data, error: queryError } = await bookCodesTable(supabase!)
          .select("id")
          .eq("claimed_by", userId)
          .limit(1);
        if (queryError) throw queryError;
        if (cancelled) return;
        if (data?.length) {
          navigate(returnPath, { replace: true });
          return;
        }
        setClaimState("ready");
      } catch (checkError) {
        if (cancelled) return;
        setError(messageFrom(checkError));
        setClaimState("error");
      }
    }

    void checkExistingClaim();
    return () => { cancelled = true; };
  }, [checkKey, isConfigured, isLoading, navigate, returnPath, session]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    const normalized = normalizeBookCode(code);
    if (!normalized) {
      setError("Enter the letters and numbers printed with your code. Spaces and dashes are okay.");
      return;
    }
    if (!supabase) {
      setError("The book-code feature isn’t available just yet. Please try again later.");
      return;
    }

    setIsSubmitting(true);
    try {
      const { error: claimError } = await claimClient(supabase).rpc("claim_book_code", { p_code: normalized });
      if (claimError) throw claimError;
      setWasClaimed(true);
      setCode("");
    } catch (claimError) {
      setError(messageFrom(claimError));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="paper-shell inner-shell">
      <header className="site-header">
        <BrandLockup />
        {session && <AccountActions />}
      </header>

      <main className="form-layout claim-layout">
        <div className="form-intro">
          <p className="eyebrow"><span className="eyebrow-rule" /> Your printed journal</p>
          <h1>Claim your<br />book code<span className="title-period">.</span></h1>
          <p className="form-description">Your one-time code connects your printed journal to this account. You can skip this and do it later.</p>
        </div>

        <section className="form-card" aria-labelledby="claim-title" aria-live="polite">
          {isLoading || (session && claimState === "checking") ? (
            <div><span className="loading-mark" aria-hidden="true" /><p className="auth-status" role="status">Checking your account…</p></div>
          ) : !session ? (
            <div className="claim-state">
              <h2 id="claim-title">Sign in to continue</h2>
              <p className="claim-help">Your book code will be linked to your Home, Twice account.</p>
              <Link href={`/login?next=${encodeURIComponent(returnPath)}`} className="primary-action full-width">
                Sign in to claim <span aria-hidden="true">→</span>
              </Link>
            </div>
          ) : wasClaimed ? (
            <div className="claim-state" role="status">
              <div className="confirmation-seal" aria-hidden="true">✓</div>
              <h2 id="claim-title">Your book is connected</h2>
              <p>Your one-time book code is now linked to your account.</p>
              <button type="button" className="primary-action full-width" onClick={() => navigate(returnPath, { replace: true })}>
                Continue <span aria-hidden="true">→</span>
              </button>
            </div>
          ) : claimState === "error" ? (
            <div className="claim-state">
              <h2 id="claim-title">We couldn’t check yet</h2>
              {error && <p className="error-message" role="alert">{error}</p>}
              <button type="button" className="primary-action full-width" onClick={() => setCheckKey((key) => key + 1)}>
                Try again <span aria-hidden="true">→</span>
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <h2 id="claim-title">Enter your book code</h2>
              <label className="field-label" htmlFor="book-code">Code from your printed journal</label>
              <input
                className="email-input"
                id="book-code"
                name="book-code"
                type="text"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                maxLength={80}
                placeholder="RIVER-4827"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                required
                aria-describedby={error ? "claim-error" : "claim-help"}
              />
              <p className="field-help" id="claim-help">You can type it with or without spaces or dashes.</p>
              {error && <p className="error-message" id="claim-error" role="alert">{error}</p>}
              <button className="primary-action full-width" type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Checking your code…" : "Claim this book"}
                {!isSubmitting && <span aria-hidden="true">→</span>}
              </button>
            </form>
          )}
          {session && !wasClaimed && (
            <button
              type="button"
              className="claim-skip"
              onClick={() => {
                rememberClaimOfferSkipped(session.user.id);
                navigate(returnPath, { replace: true });
              }}
            >
              Skip for now
            </button>
          )}
        </section>
      </main>

      {session && <div className="home-account-layout"><AccountActions placement="library" /></div>}

      <footer className="site-footer">
        <Link href={session ? "/library" : "/"} className="back-link">← {session ? "My voice pages" : "Home, Twice"}</Link>
        <nav className="legal-footer-links" aria-label="Legal">
          <Link href="/privacy">Privacy Policy</Link>
          <Link href="/terms">Terms</Link>
        </nav>
        <span>Published by Kwaderno &amp; Co.</span>
      </footer>
    </div>
  );
}
